import { useEffect, useState, useCallback } from "react";
import { UserTag } from "@/components/UserTag";

import { useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { TrancheRescheduleDialog } from "@/components/TrancheRescheduleDialog";
import RestructureDialog from "@/components/RestructureDialog";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { formatRupiah, formatNumber, formatDate, formatDateTime, todayWITA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import DatePicker from "@/components/DatePicker";
import { NotaList } from "@/components/NotaList";
import { ScheduleDates } from "@/components/ScheduleDates";
import { SupplierName } from "@/components/SupplierName";

export default function Reschedule() {
  const [searchParams] = useSearchParams();
  const supParam = searchParams.get("supplier");
  const [schedules, setSchedules] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [reschedules, setReschedules] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null); // schedule being rescheduled
  const [trDlg, setTrDlg] = useState(null); // split schedule for tranche reschedule
  const [rsDlg, setRsDlg] = useState(null);

  const load = useCallback(() => {
    api.get("/schedules?status=active").then((r) => setSchedules(r.data));
    api.get("/invoices?status=SCHEDULED").then((r) => setInvoices(r.data));
    api.get("/reschedule-history").then((r) => setReschedules(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)); }, []);
  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "-";

  const filtered = schedules.filter((s) => {
    if (supParam && s.supplier_id !== supParam) return false;
    if (!q) return true;
    const invs = invoices.filter((i) => s.invoice_ids.includes(i.id));
    return invs.some((i) => i.invoice_number.toLowerCase().includes(q.toLowerCase()) || i.invoice_date.includes(q));
  });

  return (
    <PageShell title="Reschedule Pembayaran">
      <Input placeholder="Cari no. nota / tanggal" value={q} onChange={(e) => setQ(e.target.value)} className="w-full sm:w-72" data-testid="reschedule-search" />

      <Card className="shadow-sm"><CardHeader><CardTitle className="text-base">Jadwal Aktif</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Supplier</TableHead><TableHead>Metode</TableHead><TableHead>Tgl Bayar</TableHead><TableHead>Nota</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {filtered.map((s) => {
                const invs = invoices.filter((i) => s.invoice_ids.includes(i.id));
                return (
                  <TableRow key={s.id} data-testid={`resched-sched-${s.code}`}>
                    <TableCell className="font-mono text-xs">{s.code}</TableCell>
                    <TableCell><SupplierName name={supName(s.supplier_id)} id={s.supplier_id} /></TableCell>
                    <TableCell className="capitalize">{s.method}</TableCell>
                    <TableCell><ScheduleDates s={s} testid={`rs-dates-${s.code}`} /></TableCell>
                    <TableCell className="text-xs"><NotaList numbers={invs.map((i) => i.invoice_number)} amounts={invs.map((i) => i.total_amount)} testid={`rs-notas-${s.code}`} /></TableCell>
                    <TableCell className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setDlg({ schedule: s, invoices: invs })} data-testid={`resched-btn-${s.code}`}>Reschedule</Button>
                      {s.is_split && <Button size="sm" variant="outline" onClick={() => setTrDlg(s)} data-testid={`resched-tranche-btn-${s.code}`}>Termin BG</Button>}
                      <Button size="sm" variant="outline" onClick={() => setRsDlg(s)} data-testid={`resched-split-btn-${s.code}`}>{s.is_split ? "Ubah Split" : "Split Pembayaran"}</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">Tidak ada jadwal aktif.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="shadow-sm"><CardHeader className="flex flex-row items-center justify-between"><CardTitle className="text-base">Riwayat Pengajuan Reschedule</CardTitle><Link to="/riwayat-reschedule" className="text-sm font-semibold text-blue-600 hover:underline" data-testid="resched-see-all">Lihat semua</Link></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Jenis</TableHead><TableHead>Jadwal</TableHead><TableHead>Detail</TableHead><TableHead>Tgl Lama → Baru</TableHead><TableHead>Pemohon</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead></TableRow></TableHeader>
            <TableBody>
              {reschedules.slice(0, 10).map((r) => (
                <TableRow key={r.id} data-testid={`resched-hist-${r.code}`}>
                  <TableCell className="font-mono text-xs">{r.code}</TableCell>
                  <TableCell className="text-xs">{r.kind_label}</TableCell>
                  <TableCell className="font-mono text-xs">{r.schedule_code}</TableCell>
                  <TableCell className="text-xs">{r.detail}</TableCell>
                  <TableCell className="text-xs">{formatDate(r.old_date)} → {formatDate(r.new_date)}</TableCell>
                  <TableCell><UserTag id={r.requester_id} name={r.requester} testid={`resched-hist-requester-${r.code}`} /></TableCell>
                  <TableCell><StatusBadge status={r.status} /></TableCell>
                  <TableCell>{r.approver ? <UserTag id={r.approver_id} name={r.approver} testid={`resched-hist-approver-${r.code}`} /> : "-"}</TableCell>
                </TableRow>
              ))}
              {reschedules.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">Belum ada pengajuan.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {dlg && <RescheduleDialog data={dlg} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />}
      {trDlg && <TrancheRescheduleDialog schedule={trDlg} onClose={() => setTrDlg(null)} onSaved={() => { setTrDlg(null); load(); }} />}
      {rsDlg && <RestructureDialog schedule={rsDlg} onClose={() => setRsDlg(null)} onSaved={() => { setRsDlg(null); load(); }} />}
    </PageShell>
  );
}

function RescheduleDialog({ data, onClose, onSaved }) {
  const { schedule, invoices } = data;
  const [selected, setSelected] = useState(invoices.map((i) => i.id));
  const [method, setMethod] = useState(schedule.method);
  const [payDate, setPayDate] = useState(todayWITA());
  const [warkatNo, setWarkatNo] = useState(schedule.warkat_number || "");
  const [warkatBank, setWarkatBank] = useState(schedule.warkat_bank || "");
  const [reason, setReason] = useState("");

  const submit = async () => {
    if (selected.length === 0) return toast.error("Pilih nota yang dipindahkan");
    if (!reason) return toast.error("Isi alasan reschedule");
    if (method === "giro" && (!warkatNo || !warkatBank)) return toast.error("Lengkapi data warkat");
    try {
      await api.post("/reschedules", {
        from_schedule_id: schedule.id, invoice_ids: selected, method, payment_date: payDate,
        warkat_number: method === "giro" ? warkatNo : null,
        warkat_bank: method === "giro" ? warkatBank : null, reason,
      });
      toast.success("Pengajuan reschedule dikirim, menunggu persetujuan owner");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Ajukan Reschedule · {schedule.code}</DialogTitle><DialogDescription className="sr-only">Form pengajuan reschedule jadwal pembayaran</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nota yang dipindahkan (seluruhnya)</Label>
            <div className="space-y-1 mt-1 border rounded p-2">
              {invoices.map((i) => (
                <label key={i.id} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={selected.includes(i.id)} onCheckedChange={() => setSelected(selected.includes(i.id) ? selected.filter((x) => x !== i.id) : [...selected, i.id])} data-testid={`resched-inv-${i.code}`} />
                  {i.invoice_number} · {formatNumber(i.total_rolls)} roll · {formatRupiah(i.total_amount)}
                </label>
              ))}
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Metode Baru</Label>
            <RadioGroup value={method} onValueChange={setMethod} className="flex gap-4">
              <div className="flex items-center gap-1.5"><RadioGroupItem value="transfer" id="r-t" data-testid="resched-method-transfer" /><Label htmlFor="r-t" className="text-[0.8125rem] font-normal">Transfer</Label></div>
              <div className="flex items-center gap-1.5"><RadioGroupItem value="giro" id="r-g" data-testid="resched-method-giro" /><Label htmlFor="r-g" className="text-[0.8125rem] font-normal">Giro</Label></div>
            </RadioGroup>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Tanggal Pembayaran Baru</Label><DatePicker value={payDate} onChange={setPayDate} testid="resched-date-input" /></div>
          {method === "giro" && (
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="No. Warkat" value={warkatNo} onChange={(e) => setWarkatNo(e.target.value)} data-testid="resched-warkat-no" />
              <Input placeholder="Bank" value={warkatBank} onChange={(e) => setWarkatBank(e.target.value)} data-testid="resched-warkat-bank" />
            </div>
          )}
          <div className="space-y-1.5"><Label className="text-xs">Alasan</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="resched-reason-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} data-testid="resched-submit-btn">Ajukan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
